/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {Dialog} from "../models/dialog";
import {DialogInstance, Status} from "../models/dialog-instance";

import {AuthenticationService} from "./authentication.service";
import {BaseService} from "./base-service";
import {DialogNotificationService} from "./dialog-notification.service";
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';
import { PagedResults } from 'app/models/paged-results';

@Injectable()
export class DialogService extends BaseService {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService,
    private dialogNotificationService: DialogNotificationService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/dialogs/`;
  }

  getAllDialogs(): Promise<Dialog[]> {
    const callUrl = this.getUrl();
    return this.mRestService.get<Dialog[]>(callUrl);
  }

  getDialogById(id: number): Promise<Dialog> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<Dialog>(callUrl);
  }

  createDialog(dialog: Dialog): Promise<Dialog> {
    const callUrl = this.getUrl();
    return this.mRestService.post<Dialog>(callUrl, dialog);
  }

  updateDialog(dialog: Dialog): Promise<Dialog> {
    const callUrl = this.getUrl();
    return this.mRestService.put<Dialog>(callUrl, dialog);
  }

  deleteDialog(id: number): Promise<void> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.delete(callUrl);
  }

  getAllDialogInstances(): Promise<DialogInstance[]> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.get<DialogInstance[]>(callUrl);
  }

  getDialogInstanceById(id: number): Promise<DialogInstance> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.get<DialogInstance>(callUrl);
  }

  //KHT 08/05/2021 - added sortOrder and sortColumn as variables to send to API so the sorting can be done by the backend
  getAllDialogInstancesOfStatus(status: Status, start: number, size: number, sortOrder: string, sortColumn: string): Promise<DialogInstance[]> {
    const callUrl = this.getUrl() + `instances/status/${status}?start=${start}&size=${size}&sortOrder=${sortOrder}&sortColumn=${sortColumn}`;
    console.log(`getAllDialogInstancesOfStatus(${callUrl})`)
    return this.mRestService.get<DialogInstance[]>(callUrl);
  }

  createDialogInstance(dialogInstance: DialogInstance): Promise<DialogInstance> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.post<DialogInstance>(callUrl, dialogInstance);
  }

  updateDialogInstance(dialogInstance: DialogInstance): Promise<void | DialogInstance> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.put<DialogInstance>(callUrl, dialogInstance)
      .then(response => {
        this.dialogNotificationService.refreshDialogs();
      })
  }

  updateDialogInstanceWithButton(dialogInstance: DialogInstance, dialogButtonId: Number): Promise<void | DialogInstance> {
    const callUrl = this.getUrl() + `instances/button/` + dialogButtonId;
    return this.mRestService.put<DialogInstance>(callUrl, dialogInstance)
      .then(response => {
        this.dialogNotificationService.refreshDialogs();
      })
  }

  deleteDialogInstance(id: number): Promise<null> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.delete(callUrl);
  }

  getDialogInstanceStatusCount(status: Status): Promise<number> {
    const callUrl = this.getUrl() + `instances/status/${status}/count`;
    return this.mRestService.get<number>(callUrl);
  }

  getAllDialogsForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<Dialog>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;
    
    return this.mRestService.get<PagedResults<Dialog>>(callUrl);
  }

  getRulesUsingDialog(dialog: Dialog): Promise<any> {
    const callUrl = this.getUrl() + `rules/${dialog.id}`;
    return this.mRestService.get<any>(callUrl);
  }

}
